import { useParams, useNavigate } from "react-router-dom";
import { useState } from "react";
import Header from "./Header";
import {
  useGetFollowersQuery,
  useRemoveFollowerMutation,
  useFollowUserMutation,
  useUnfollowUserMutation,
} from "../store/api/followApi";
import { useAuth } from "../hooks/useAuth";
import styles from "./FollowListPage.module.css";

export default function FollowersPage() {
  const { userId: userIdParam } = useParams();
  const navigate = useNavigate();
  const { userId } = useAuth();

  const [removeFollower] = useRemoveFollowerMutation();
  const [loadingId, setLoadingId] = useState(null);

  const [followUser] = useFollowUserMutation();
  const [unfollowUser] = useUnfollowUserMutation();
  const [loadingFollowId, setLoadingFollowId] = useState(null);

  const handleFollowToggle = async (e, targetId, isFollowing) => {
    e.stopPropagation();
    setLoadingFollowId(targetId);

    try {
      if (isFollowing) {
        await unfollowUser(targetId).unwrap();
      } else {
        await followUser(targetId).unwrap();
      }
    } catch (err) {
      console.error("Follow toggle failed", err);
    } finally {
      setLoadingFollowId(null);
    }
  };
  // ✅ Fix logic (same as FollowingPage)
  const targetUserId = userIdParam ?? userId;
  const isOwnProfile = targetUserId === userId;

  const {
    data: followers,
    isLoading,
    isError,
  } = useGetFollowersQuery(targetUserId);

  const handleRemoveFollower = async (e, followerId) => {
    e.stopPropagation();
    setLoadingId(followerId);

    try {
      await removeFollower(followerId).unwrap();
    } catch (err) {
      console.error("Failed to remove follower", err);
    } finally {
      setLoadingId(null);
    }
  };

  if (isLoading) {
    return (
      <>
        <Header />
        <div className={styles.container}>
          <p>Loading followers...</p>
        </div>
      </>
    );
  }

  if (isError) {
    return (
      <>
        <Header />
        <div className={styles.container}>
          <p>Failed to load followers</p>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />

      <div className={styles.container}>
        <div className={styles.header}>
          <button onClick={() => navigate(-1)} className={styles.backBtn}>
            ← Back
          </button>

          <h1>Followers ({followers?.length || 0})</h1>

          {/* Spacer for symmetry */}
          <div className={styles.headerSpacer} />
        </div>

        <div className={styles.listContainer}>
          {followers && followers.length > 0 ? (
            followers.map((user) => (
              <div
                key={user.id}
                className={styles.userCard}
                onClick={() => navigate(`/profile/${user.id}`)}
              >
                {user.profilePhotoUrl ? (
                  <img
                    src={user.profilePhotoUrl}
                    alt={user.name}
                    className={styles.avatar}
                  />
                ) : (
                  <div className={styles.noPhoto}>
                    {user.name
                      ?.split(" ")
                      .map((w) => w[0])
                      .slice(0, 2)
                      .join("")
                      .toUpperCase() || "?"}
                  </div>
                )}

                <div className={styles.userInfo}>
                  <h3>{user.name}</h3>
                  <p className={styles.userType}>{user.userType}</p>

                  {user.bio && <p className={styles.bio}>{user.bio}</p>}
                </div>

                {/* When viewing your own profile */}
                {isOwnProfile && (
                  <button
                    className={styles.removeBtn}
                    disabled={loadingId === user.id}
                    onClick={(e) => handleRemoveFollower(e, user.id)}
                  >
                    {loadingId === user.id ? "..." : "✕ Remove"}
                  </button>
                )}

                {/* When viewing someone else's followers */}
                {!isOwnProfile && user.id !== userId && (
                  <button
                    className={
                      user.isFollowing ? styles.unfollowBtn : styles.followBtn
                    }
                    disabled={loadingFollowId === user.id}
                    onClick={(e) =>
                      handleFollowToggle(e, user.id, user.isFollowing)
                    }
                  >
                    {loadingFollowId === user.id
                      ? "..."
                      : user.isFollowing
                        ? "Unfollow"
                        : "Follow"}
                  </button>
                )}
              </div>
            ))
          ) : (
            <div className={styles.emptyState}>
              <p>No followers yet</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
